/**
 * ZigZag Indicator
 *
 * Connects significant swing highs and lows, filtering out moves
 * smaller than the threshold percentage. Useful for wave analysis.
 *
 * Category: Support/Resistance | Pane: Overlay
 */
import { IndicatorDefinition, PlotData } from "../types/indicator.types";
import { CandleData } from "../../types/chart.types";

export const zigzagIndicator: IndicatorDefinition = {
  id: "zigzag",
  name: "ZigZag",
  shortName: "ZZ",
  category: "support_resistance",
  pane: "overlay",
  params: [
    { key: "threshold", label: "Threshold %", type: "number", default: 5, min: 0.5, max: 50, step: 0.5 },
    { key: "color", label: "Color", type: "color", default: "#FF9800" },
  ],
  plots: [
    { key: "zigzag", label: "ZigZag", color: "#FF9800", style: "line", lineWidth: 2 },
  ],
  calculate: (candles: CandleData[], params: Record<string, any>): PlotData[] => {
    const { threshold } = params;
    const data: { time: number; value: number }[] = [];

    if (candles.length < 2) return [{ key: "zigzag", data }];

    const pct = threshold / 100;
    const pivots: { idx: number; value: number; type: "high" | "low" }[] = [];

    let lastPivot = { idx: 0, value: candles[0].close, type: "high" as "high" | "low" };
    let currentHigh = { idx: 0, value: candles[0].high };
    let currentLow = { idx: 0, value: candles[0].low };

    for (let i = 1; i < candles.length; i++) {
      if (candles[i].high > currentHigh.value) {
        currentHigh = { idx: i, value: candles[i].high };
      }
      if (candles[i].low < currentLow.value) {
        currentLow = { idx: i, value: candles[i].low };
      }

      if (lastPivot.type === "high") {
        if (candles[i].low <= lastPivot.value * (1 - pct)) {
          pivots.push({ idx: currentHigh.idx, value: currentHigh.value, type: "high" });
          lastPivot = { idx: i, value: candles[i].low, type: "low" };
          currentLow = { idx: i, value: candles[i].low };
          currentHigh = { idx: i, value: candles[i].high };
        }
      } else {
        if (candles[i].high >= lastPivot.value * (1 + pct)) {
          pivots.push({ idx: currentLow.idx, value: currentLow.value, type: "low" });
          lastPivot = { idx: i, value: candles[i].high, type: "high" };
          currentHigh = { idx: i, value: candles[i].high };
          currentLow = { idx: i, value: candles[i].low };
        }
      }
    }

    // Add final pivot
    if (lastPivot.type === "high") {
      pivots.push({ idx: currentHigh.idx, value: currentHigh.value, type: "high" });
    } else {
      pivots.push({ idx: currentLow.idx, value: currentLow.value, type: "low" });
    }

    // Build line data from pivots
    for (const p of pivots) {
      data.push({ time: candles[p.idx].time, value: p.value });
    }

    return [{ key: "zigzag", data }];
  },
};
